/* eslint-disable @typescript-eslint/no-explicit-any */

import { fromStore, readable, writable, type Readable, type Writable } from 'svelte/store';

import { watch } from '../reactive-store';
import { type AnyReactiveState } from '../reactive-store.helper';

// getter overloads: re-subscribes when the source changes

// readable store, without setter, subproperties can still be reactively set.
// Einfachste Version: "useReactiveStore(reactiveState: T) => Readable<T>"
export function useReactiveState<T extends object>(state: T | (() => T) | Readable<T>): Readable<T>;

export function useReactiveState<T extends object, Getter extends (value: T) => any>(
  state: T | (() => T) | Readable<T>,
  lens: { get: Getter; set?: never },
): Readable<ReturnType<Getter>>;

// writable store, braucht einen setter, falls man den store direct tauschen will oder ein primitive property.
export function useReactiveState<
  T extends object,
  Getter extends (rs: T) => any,
  Setter extends (reactiveState: T, value: ReturnType<Getter>) => void,
>(
  state: T | (() => T) | Readable<T>,
  lens: { set: Setter; get: Getter },
): Omit<Writable<Getter extends (v: T) => infer R ? R : T>, 'set'> & {
  set: (value: Parameters<Setter>[1]) => void;
};

export function useReactiveState<T extends object, K extends keyof T>(
  state: T | (() => T) | Readable<T>,
  lens: K,
): Writable<T[K]>;

/**
 * Creates a reactive store from a reactive state.
 * @param state - The targeted reactive state (this will be watched)
 * @param lens - optional getter and setters to modify the state value and set function for svelte
 * @returns A reactive store.
 */
export function useReactiveState<T extends AnyReactiveState>(
  stateOrGetter: T | (() => T) | Readable<T>,
  lens?: { set?: (reactiveState: T, value: any) => void; get?: (value: T) => any } | keyof T,
  debug = false,
) {
  const debugLog = debug ? (...args: any[]) => console.log(...args) : () => {};
  const getter =
    typeof lens === 'object' ? lens?.get
    : lens ? (v: T) => v[lens]
    : undefined;
  const setter =
    typeof lens === 'object' ? lens?.set
    : lens ? (v: T, value: T[typeof lens]) => (v[lens] = value)
    : undefined;
  const getValue = getter ? (v: T) => getter(v) : (v: T) => v;
  if (typeof stateOrGetter === 'function') {
    const sourceGetter = stateOrGetter as () => T;
    const { subscribe, set } = writable(getValue(sourceGetter()));

    $effect(() => {
      const state = sourceGetter();
      set(getValue(state));
      if (getter) {
        return watch(state, (v) => set(v), getter);
      } else {
        return watch(state, (v) => set(v as any));
      }
    });

    if (setter) {
      return {
        subscribe,
        set: (value: any) => {
          setter(sourceGetter(), value);
          set(value);
        },
      };
    }

    // Svelte's store_mutate (bind:value, $store.prop = v) calls store.set() internally.
    // The proxy mutation already triggers watch → writable.set, so this is just a no-op
    // to prevent "store.set is not a function".
    return { subscribe, set: () => {} };
  }

  if (typeof stateOrGetter === 'object' && 'subscribe' in stateOrGetter) {
    const state = fromStore(stateOrGetter);
    const sourceGetter = () => state.current;
    const { subscribe, set } = writable(getValue(sourceGetter()));

    $effect(() => {
      const state = sourceGetter();
      set(getValue(state));
      if (getter) {
        return watch(state, (v) => set(v), getter);
      } else {
        return watch(state, (v) => set(v as any));
      }
    });

    if (setter) {
      return {
        subscribe,
        set: (value: any) => {
          setter(sourceGetter(), value);
          set(value);
        },
      };
    }

    // Svelte's store_mutate (bind:value, $store.prop = v) calls store.set() internally.
    // The proxy mutation already triggers watch → writable.set, so this is just a no-op
    // to prevent "store.set is not a function".
    return { subscribe, set: () => {} };
  }

  const state = stateOrGetter;

  debugLog('?????? useReactiveStore', state);

  if (!setter) {
    debugLog('!!!>>>>>>>>>>>> useReactiveStore readable', getValue(state));

    return readable(getValue(state), (set) => {
      if (getter) {
        return watch(
          state,
          (value) => {
            debugLog('!!!!!!>>>>>>>>>>>> useReactiveStore readable', value);
            set(value);
          },
          getter,
        );
      } else {
        return watch(state, (value) => set(value));
      }
    });
  }

  debugLog('!!!>>>>>>>>>>>> useReactiveStore writable', getValue(state));

  const { subscribe, set } = writable(getValue(state), (set) => {
    if (getter) {
      return watch(state, (value) => set(value), getter);
    } else {
      return watch(state, (value) => set(value));
    }
  });

  return {
    subscribe,
    set: (value: any) => {
      setter(state, value);
      set(value);
    },
  };
}

/**
 * Svelte state eines reactive stores.
 * @param store - The targeted reactive store
 * @param options - optional getter and setters to modify the state value and set function for svelte
 * @returns A reactive state.
 */
// getter overloads: re-subscribes when the source changes
export function useReactiveStoreState<T extends AnyReactiveState>(
  getter: () => T,
): { readonly current: T };
export function useReactiveStoreState<T extends AnyReactiveState, Getter extends (value: T) => any>(
  getter: () => T,
  options: { get: Getter },
): { readonly current: ReturnType<Getter> };
export function useReactiveStoreState<
  T extends AnyReactiveState,
  Getter extends (value: T) => any,
  Setter extends (reactiveState: T, value: ReturnType<Getter>) => void,
>(getter: () => T, options: { set: Setter; get: Getter }): { current: ReturnType<Getter> };

export function useReactiveStoreState<T extends AnyReactiveState>(
  store: T,
): { readonly current: T };
export function useReactiveStoreState<T extends AnyReactiveState, Getter extends (value: T) => any>(
  store: T,
  options: { get: Getter },
): { readonly current: ReturnType<Getter> };
export function useReactiveStoreState<
  T extends AnyReactiveState,
  Getter extends (value: T) => any,
  Setter extends (reactiveState: T, value: ReturnType<Getter>) => void,
>(store: T, options: { set: Setter; get: Getter }): { current: ReturnType<Getter> };
export function useReactiveStoreState<
  T extends AnyReactiveState,
  Getter extends (value: T) => any,
  Setter extends (reactiveState: T, value: ReturnType<Getter>) => void,
>(
  storeOrGetter: T | (() => T),
  options?: { get: Getter; set?: Setter },
): { current: ReturnType<Getter> } | { readonly current: ReturnType<Getter> } {
  if (typeof storeOrGetter === 'function') {
    const sourceGetter = storeOrGetter as () => T;
    const select = options?.get;
    const setter = options?.set;
    let value = $state(select ? select(sourceGetter()) : sourceGetter());

    $effect(() => {
      const state = sourceGetter();
      value = select ? select(state) : state;
      if (select) {
        return watch(
          state,
          (v) => {
            value = v;
          },
          select,
        );
      } else {
        return watch(state, (v) => {
          value = v;
        });
      }
    });

    if (setter) {
      return {
        get current() {
          return value;
        },
        set current(v: any) {
          setter(sourceGetter(), v);
        },
      };
    }

    return {
      get current() {
        return value;
      },
    };
  }

  const store = storeOrGetter;
  const get = options?.get;
  const set = options?.set;
  if (!options || !get) {
    return fromStore(useReactiveState(store)) as any;
  }
  if (!set) {
    return fromStore(useReactiveState(store, { get })) as any;
  }
  return fromStore(useReactiveState(store, { get, set })) as any;
}
